import { toaster } from '@optiaxiom/react';

import http, { extractApiErrorMessage } from '@frontend/api/http';
import { HYPATIA_URL } from '@frontend/envConfig';
import type { AgentVersionListResponse } from '@frontend/types';
import {
  Trigger,
  WorkflowAgent,
  WorkflowAgentExecution,
  WorkflowAgentTriggerResponse,
} from '@frontend/types/workflow-agent';

import { createEmailTrigger } from '../components/workflow-agent/services/email-trigger';
import { convertToWorkflowAgent } from '../components/workflow-agent/services/graph-to-workflow';
import { createMessageTrigger } from '../components/workflow-agent/services/message-trigger';
import { createScheduledTrigger } from '../components/workflow-agent/services/scheduled-trigger';
import { createWebhookTrigger } from '../components/workflow-agent/services/webhook-trigger';
import { triggerBlobDownload } from '../utils/downloadFileResponse';

import type { ImportValidationReport } from './import-validation-types';

export const createWorkflowTriggers = async (
  instanceId: string,
  workflowId: string,
  triggers: Trigger[]
) => {
  const triggerPromises: Array<{ promise: Promise<unknown>; type: string }> = [];

  // Create a mapping of trigger types to their creation functions
  const triggerCreators = {
    webhook: createWebhookTrigger,
    email: createEmailTrigger,
    scheduled: createScheduledTrigger,
    message: createMessageTrigger,
  } as const;

  // Loop through all triggers and push to triggerPromises based on type
  triggers.forEach(trigger => {
    if (trigger.type === 'webhook') {
      triggerPromises.push({
        promise: triggerCreators.webhook(
          workflowId,
          instanceId,
          trigger as unknown as Record<string, unknown>
        ),
        type: trigger.type,
      });
    } else if (trigger.type === 'email') {
      triggerPromises.push({
        promise: triggerCreators.email(
          workflowId,
          instanceId,
          trigger as unknown as Record<string, unknown>
        ),
        type: trigger.type,
      });
    } else if (trigger.type === 'scheduled') {
      triggerPromises.push({
        promise: triggerCreators.scheduled(
          workflowId,
          instanceId,
          trigger as unknown as Record<string, unknown>
        ),
        type: trigger.type,
      });
    } else if (trigger.type === 'message') {
      triggerPromises.push({
        promise: triggerCreators.message(
          workflowId,
          instanceId,
          trigger as unknown as Record<string, unknown>
        ),
        type: trigger.type,
      });
    } else {
      console.warn(`No creator function found for trigger type: ${(trigger as Trigger).type}`);
    }
  });

  if (triggerPromises.length > 0) {
    const results = await Promise.allSettled(triggerPromises.map(item => item.promise));

    results.forEach((result, index) => {
      const triggerType = triggerPromises[index].type;
      if (result.status === 'rejected') {
        console.error(`Failed to create ${triggerType} trigger:`, result.reason);
        toaster.create(extractApiErrorMessage(result.reason), { intent: 'danger' });
      }
    });
  }
};

export const createWorkflowAgent = async (
  agent: WorkflowAgent,
  instanceId: string
): Promise<WorkflowAgent> => {
  const data = convertToWorkflowAgent(agent.nodes || [], agent.edges || [], agent);

  try {
    const response = await http.post<WorkflowAgent>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/workflows`,
      data.agentData
    );
    return response.data;
  } catch (error) {
    console.error('Failed to save workflow agent:', error);
    throw error;
  }
};

export const updateWorkflowAgent = async (
  agent: WorkflowAgent,
  updateUrl: string
): Promise<WorkflowAgent> => {
  try {
    const { agentData, triggers } = convertToWorkflowAgent(
      agent.nodes || [],
      agent.edges || [],
      agent
    );

    const updatePayload = {
      ...agentData,
      triggers,
    };

    const response = await http.put<WorkflowAgent>(updateUrl, updatePayload);
    return response.data;
  } catch (error) {
    console.error('Failed to update workflow agent:', error);
    throw error;
  }
};

export const getWorkflowAgents = (
  instanceId: string,
  page = 1,
  pageSize = 1000,
  includeDeleted = false,
  includeSteps = false
) => ({
  key: () =>
    ['opal:workflow-agents', instanceId, page, pageSize, includeDeleted, includeSteps] as const,
  queryFn: () => async (): Promise<WorkflowAgent[]> => {
    const response = await http.get<{ items: WorkflowAgent[] }>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/workflows?page=${page}&page_size=${pageSize}&include_deleted=${includeDeleted}&include_steps=${includeSteps}`
    );
    return response.data.items || [];
  },
});

export const getWorkflowAgent = (url: string) => async (): Promise<WorkflowAgent> => {
  const response = await http.get<WorkflowAgent>(url);
  return response.data;
};

export const deleteWorkflowAgent = async (deleteUrl: string): Promise<void> => {
  try {
    await http.delete(deleteUrl);
    return;
  } catch (error) {
    console.error('Failed to delete workflow agent:', error);
    throw error;
  }
};

export const triggerWorkflowAgent = async (
  instanceId: string,
  agentId: string,
  inputText: string
): Promise<WorkflowAgentTriggerResponse> => {
  try {
    const response = await http.post<WorkflowAgentTriggerResponse>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/workflows/${agentId}/trigger`,
      inputText,
      {
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to trigger workflow agent:', error);
    throw new Error('Failed to trigger workflow agent');
  }
};

export const getWorkflowAgentExecution = async (
  instanceId: string,
  executionId: string
): Promise<WorkflowAgentExecution> => {
  const response = await http.get<WorkflowAgentExecution>(
    `${HYPATIA_URL}/api/v1/${instanceId}/workflow-executions/${executionId}`
  );
  return response.data;
};

export const exportWorkflowAgent = async (instanceId: string, agentId: string) => {
  const response = await http.get<{ data: unknown }>(
    `${HYPATIA_URL}/api/v1/${instanceId}/agents/workflows/${agentId}/export`
  );
  return response.data.data;
};

export const validateWorkflowAgentImport = async (instanceId: string, agentData: object) => {
  const res = await http.post<{ data: ImportValidationReport }>(
    `${HYPATIA_URL}/api/v1/${instanceId}/agents/workflows/import/validate`,
    agentData
  );
  return res.data.data;
};

export const importWorkflowAgent = async (
  instanceId: string,
  agentData: object,
  mode: 'skip' | 'overwrite' = 'overwrite',
  conflictResolutions?: Record<string, string>,
  triggerConfigs?: Record<string, Record<string, unknown>>,
  directoryInstall?: { directory_agent_id: string; directory_version: number }
) => {
  const hasExtras =
    (conflictResolutions && Object.keys(conflictResolutions).length > 0) ||
    (triggerConfigs && Object.keys(triggerConfigs).length > 0) ||
    directoryInstall;

  if (hasExtras) {
    const res = await http.post(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/workflows/import?mode=${mode}`,
      {
        agent_data: agentData,
        conflict_resolutions: conflictResolutions ?? {},
        trigger_configs: triggerConfigs ?? {},
        ...(directoryInstall && {
          directory_agent_id: directoryInstall.directory_agent_id,
          directory_version: directoryInstall.directory_version,
        }),
      }
    );
    return res.data;
  }
  const res = await http.post(
    `${HYPATIA_URL}/api/v1/${instanceId}/agents/workflows/import?mode=${mode}`,
    agentData
  );
  return res.data;
};

// Export workflow execution logs as CSV or Excel file download.
// When a date range is set, `limit` is omitted (the backend rejects limit+range)
// and the timeout is raised to accommodate the batched/ZIP path.
export async function exportWorkflowExecutionLogs(
  instanceId: string,
  workflowAgentId: string,
  fields: string[],
  format: 'csv' | 'xlsx' = 'csv',
  startDate?: string,
  endDate?: string,
  limit = 500
): Promise<void> {
  const hasRange = Boolean(startDate || endDate);
  const body: Record<string, unknown> = { workflow_agent_id: workflowAgentId, fields, format };
  if (hasRange) {
    if (startDate) body.start_date = startDate;
    if (endDate) body.end_date = endDate;
  } else {
    body.limit = limit;
  }

  const res = await http.post(
    `${HYPATIA_URL}/api/v1/${instanceId}/workflow-executions/export`,
    body,
    {
      responseType: 'blob',
      timeout: hasRange ? 10 * 60 * 1000 : 5 * 60 * 1000,
    }
  );

  triggerBlobDownload(
    res.data as Blob,
    res.headers['content-disposition'] as string | undefined,
    `workflow-executions.${format}`
  );
}

export function getWorkflowAgentVersions(
  instanceId: string,
  agentId: string,
  limit = 20,
  offset = 0
) {
  return async () => {
    const res = await http.get<AgentVersionListResponse>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/workflows/${agentId}/versions`,
      { params: { limit, offset } }
    );
    return res.data;
  };
}

getWorkflowAgentVersions.key = (instanceId: string, agentId: string, limit = 20, offset = 0) =>
  ['opal:workflow-agent-versions', instanceId, agentId, limit, offset] as const;

export function getWorkflowAgentVersion(versionUrl: string) {
  return async () => {
    const res = await http.get<WorkflowAgent>(versionUrl);
    return res.data;
  };
}

getWorkflowAgentVersion.key = (versionUrl: string) =>
  ['opal:workflow-agent-version', versionUrl] as const;

export async function restoreWorkflowAgent(
  updateUrl: string,
  agentData: Record<string, unknown>
): Promise<WorkflowAgent> {
  const response = await http.put<WorkflowAgent>(updateUrl, agentData);
  return response.data;
}
